import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPackage, FiSearch, FiRefreshCw, FiChevronDown, FiChevronUp, FiPrinter, FiDownload } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { formatOrderReference } from '../../utils/orderReference';
import { getSellerOrderTotals, sumLineTotals } from '../../utils/sellerOrderTotals';
import {
  DesktopOnly,
  MobileOnly,
  MobileOrderList,
  MobileOrderCard,
  MobileOrderHeader,
  MobileOrderHeaderMain,
  MobileOrderHeaderAside,
  MobileOrderId,
  MobileOrderMeta,
  MobileOrderTotal,
  MobileBadgeRow,
  MobileOrderBody,
  mobileCardStyles,
  mobileActionButtonStyles,
  mobileActionContainerStyles,
} from '../../styles/orderMobileLayout';

const isSystemInternationalNote = (note) => {
  const normalized = String(note || '').trim().toLowerCase();
  return normalized.startsWith('international order to ') && normalized.includes('requires manual shipment setup');
};

const sanitizeOrderItemNote = (note) => (isSystemInternationalNote(note) ? null : note);

const Container = styled.div`
  padding: 32px;
  max-width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const DateInput = styled.input`
  padding: 10px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #1f2937;
  min-width: 150px;
  outline: none;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.2);
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  ${mobileCardStyles}
`;

const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
    
    &:hover {
      background: #9ca3af;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 800px;
  direction: ${props => props.$isArabic ? 'rtl' : 'ltr'};
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: ${props => props.$isArabic ? 'right' : 'left'};
  font-weight: 600;
  font-size: 13px;
  color: #374151;
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: sticky;
  top: 0;
  z-index: 10;
  
  &:first-child {
    ${props => props.$isArabic ? 'padding-right: 24px;' : 'padding-left: 24px;'}
    ${props => props.$isArabic ? 'border-top-right-radius: 8px;' : 'border-top-left-radius: 8px;'}
  }
  
  &:last-child {
    ${props => props.$isArabic ? 'padding-left: 24px;' : 'padding-right: 24px;'}
    ${props => props.$isArabic ? 'border-top-left-radius: 8px;' : 'border-top-right-radius: 8px;'}
  }
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #f3f4f6;
  background: white;
  vertical-align: middle;
  text-align: ${props => props.$isArabic ? 'right' : 'left'};
  
  &:first-child {
    ${props => props.$isArabic ? 'padding-right: 24px;' : 'padding-left: 24px;'}
  }
  
  &:last-child {
    ${props => props.$isArabic ? 'padding-left: 24px;' : 'padding-right: 24px;'}
  }
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb !important;
    transform: scale(1.001);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    
    ${TableCell} {
      background: #f9fafb;
    }
  }
  
  &:nth-child(even) {
    ${TableCell} {
      background: #fafbfc;
    }
  }

  &:last-child ${TableCell} {
    border-bottom: none;
  }
  
  /* Add subtle left border accent on hover */
  &:hover ${TableCell}:first-child {
    border-left: 3px solid #5efba9;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  
  background: ${props => {
    if (props.status === 'completed') return 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
    if (props.status === 'pending') return 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
    if (props.status === 'processing') return 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)';
    if (props.status === 'out_for_delivery') return 'linear-gradient(135deg, #CCFBF1 0%, #5EEAD4 100%)';
    if (props.status === 'canceled') return 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
    if (props.status === 'paid') return 'linear-gradient(135deg, #065f46 0%, #047857 100%)';
    return 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
  }};
  
  color: ${props => {
    if (props.status === 'completed') return '#065f46';
    if (props.status === 'pending') return '#92400e';
    if (props.status === 'processing') return '#1e40af';
    if (props.status === 'out_for_delivery') return '#0F766E';
    if (props.status === 'canceled') return '#991b1b';
    if (props.status === 'paid') return '#fff';
    return '#065f46';
  }};
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: ${props => props.status === 'processing' ? 'pulse 2s infinite' : 'none'};
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const ExpandedContent = styled.div`
  padding: 24px;
  background: linear-gradient(to bottom, #fafbfc 0%, #ffffff 100%);
  border-top: 2px solid #f3f4f6;
  animation: slideDown 0.3s ease;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ExpandedSection = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #f3f4f6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    width: 3px;
    height: 16px;
    background: linear-gradient(to bottom, #5efba9, #4de89e);
    border-radius: 2px;
  }
`;

const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 16px;

  &:hover {
    background: #4de89e;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  ${mobileActionButtonStyles}
`;

const WhatsAppButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 16px;
  text-decoration: none;
  margin-left: 8px;

  &:hover {
    background: #20BA5A;
    transform: translateY(-1px);
    color: white;
  }

  &:active {
    transform: translateY(0);
  }

  ${mobileActionButtonStyles}
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  ${mobileActionContainerStyles}
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    transform: translateX(4px);
  }
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #f3f4f6;
  transition: all 0.2s ease;
  
  ${ItemRow}:hover & {
    border-color: #5efba9;
    transform: scale(1.05);
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ItemName = styled.div`
  font-weight: 500;
  color: #1f2937;
`;

const ItemMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 24px auto;
  padding: 12px 32px;
  background: #5efba9;
  border: 1px solid #5efba9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4dd699;
    border-color: #4dd699;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusSelect = styled.select`
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  border: none;
  background: ${props => {
    if (props.value === 'completed') return '#d1fae5';
    if (props.value === 'pending') return '#fef3c7';
    if (props.value === 'processing') return '#dbeafe';
    if (props.value === 'canceled') return '#fee2e2';
    return '#e5e7eb';
  }};
  color: ${props => {
    if (props.value === 'completed') return '#065f46';
    if (props.value === 'pending') return '#92400e';
    if (props.value === 'processing') return '#1e40af';
    if (props.value === 'canceled') return '#991b1b';
    return '#374151';
  }};
  cursor: pointer;
  outline: none;

  &:focus {
    outline: 2px solid #5efba9;
    outline-offset: 2px;
  }
`;

// Status step labels
const statusSteps = [
  "Shipping Request",
  "Received in the store",
  "Out To Deliver",
  "Delivered To Consignee",
];

const normalizeStatusName = (shipmentStatusName, isArabic = false) => {
  if (!shipmentStatusName || shipmentStatusName === '') return isArabic ? 'قيد الانتظار' : 'Pending';

  const status = shipmentStatusName.toLowerCase().trim();

  if (status.includes('shipping request') || status.includes('طلب شحن'))
    return isArabic ? 'طلب شحن' : 'Shipping Request';

  if (status.includes('received in the store') || status.includes('الاستلام') || status.includes('المخزن'))
    return isArabic ? 'تم الاستلام بالمخزن' : 'Received in the store';

  if (
    status.includes('out to deliver') ||
    status.includes('out for delivery') ||
    status.includes('جاري التوصيل') ||
    status.includes('قيد التوصيل')
  ) return isArabic ? 'جاري التوصيل' : 'Out for Delivery';

  if (status.includes('delivered') || status.includes('تم التسليم'))
    return isArabic ? 'تم التسليم' : 'Delivered';

  if (status.includes('refund'))
    return isArabic ? 'مسترد' : 'Refunded';

  if (status.includes('cancel'))
    return isArabic ? 'ملغي' : 'Canceled';

  return shipmentStatusName.charAt(0).toUpperCase() + shipmentStatusName.slice(1);
};

const getStatusTone = (statusText) => {
  const status = String(statusText || '').toLowerCase().trim();
  if (!status) return 'pending';
  if (status.includes('cancel')) return 'canceled';
  if (status.includes('out to deliver') || status.includes('out for delivery')) return 'out_for_delivery';
  if (status.includes('delivered') || status.includes('complete')) return 'completed';
  if (
    status.includes('processing') ||
    status.includes('shipping request') ||
    status.includes('received in the store')
  ) return 'processing';
  return 'pending';
};

function AdminOrders() {
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [displayedCount, setDisplayedCount] = useState(20);
  const [customFieldsMap, setCustomFieldsMap] = useState({}); // key_name -> {label, label_ar}

  useEffect(() => {
    if (session) {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const fetchOrders = async () => {
    if (!session) return;

    try {
      setLoading(true);
      console.log('Fetching orders for admin brand...');

      // Get the admin's brand_id from brand_managers
      // Get the user's brand_id
      let brandId = null;
      
      const { data: brandManager, error: brandError } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .maybeSingle();

      if (brandManager && brandManager.brand_id) {
        brandId = brandManager.brand_id;
      } else {
        // Fallback: Try to find brand by created_by (use most recent if multiple)
        const { data: userBrands, error: brandsError } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (!brandsError && userBrands && userBrands.length > 0) {
          brandId = userBrands[0].id;
          if (userBrands.length > 1) {
            console.warn('⚠️ User has multiple brands. Using the most recent one.');
          }
        }
      }

      if (!brandId) {
        console.error('Error: No brand found for user');
        console.log('User ID:', session.user.id);
        console.log('Brand Manager Data:', brandManager);
        setOrders([]);
        setLoading(false);
        return;
      }

      console.log('Found brand ID:', brandId);

      // Fetch orders with order items for this brand (only paid orders)
      // First, try to get order items filtered by brand_id
      let { data: orderItems, error: itemsError } = await supabase
        .from('order_items')
        .select(`
              id,
              product_id,
              variant_id,
              quantity,
              line_total,
              tracking_url,
              shipment_status_name,
              shipment_status_id,
              shipment_id,
              note,
              attributes,
              brand_id,
              orders!inner(
               *
              ),
              products(
                id,
                name,
                images
              ),
              variant:product_variants(
                id,
                sku,
                variant_options:product_variant_options(
                  option_group:product_option_groups(
                    id,
                    name,
                    label
                  ),
                  option_value:product_option_values(
                    id,
                    value
                  )
                )
              )
            `)
        .eq('brand_id', brandId)
        .eq('orders.payment_status', 'paid');

      if (itemsError) {
        console.error('Error fetching order items:', itemsError);
        console.error('Error details:', {
          message: itemsError.message,
          details: itemsError.details,
          hint: itemsError.hint,
          code: itemsError.code
        });
        alert(isArabic ? `خطأ في جلب الطلبات: ${itemsError.message}` : `Error fetching orders: ${itemsError.message}`);
        setOrders([]);
        setLoading(false);
        return;
      }

      console.log('Order items fetched:', orderItems?.length || 0);
      console.log('Sample order item:', orderItems?.[0]);
      if (orderItems?.[0]) {
        console.log('Sample order item variant:', orderItems[0].variant);
        console.log('Sample order item variant_options:', orderItems[0].variant?.variant_options);
      }

      // If no order items found with brand_id filter, try filtering through products
      if (!orderItems || orderItems.length === 0) {
        console.log('No order items found with brand_id filter, trying through products...');
        
        // Try alternative query: filter through products
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('id')
          .eq('brand_id', brandId);

        if (productsError) {
          console.error('Error fetching products:', productsError);
        } else if (productsData && productsData.length > 0) {
          const productIds = productsData.map(p => p.id);
          console.log(`Found ${productIds.length} products for this brand, trying to fetch order items...`);
          
          const { data: orderItemsByProduct, error: itemsByProductError } = await supabase
            .from('order_items')
            .select(`
                  id,
                  product_id,
                  variant_id,
                  quantity,
                  line_total,
                  tracking_url,
                  shipment_status_name,
                  shipment_status_id,
                  shipment_id,
                  note,
                  brand_id,
                  orders!inner(
               *
              ),
                  products(
                    id,
                    name,
                    images
                  ),
                  variant:product_variants(
                    id,
                    sku,
                    variant_options:product_variant_options(
                      option_group:product_option_groups(
                        id,
                        name,
                        label
                      ),
                      option_value:product_option_values(
                        id,
                        value
                      )
                    )
                  )
                `)
            .in('product_id', productIds)
            .eq('orders.payment_status', 'paid');

          if (!itemsByProductError && orderItemsByProduct && orderItemsByProduct.length > 0) {
            console.log('Found order items through products:', orderItemsByProduct.length);
            // Use the order items found through products
            orderItems = orderItemsByProduct;
          } else {
            console.log('No order items found through products either');
            console.log('Brand ID used:', brandId);
            setOrders([]);
            setLoading(false);
            return;
          }
        } else {
          console.log('No products found for this brand');
          console.log('Brand ID used:', brandId);
          setOrders([]);
          setLoading(false);
          return;
        }
      }

      // Get unique profile IDs to fetch emails
      const profileIds = [...new Set(orderItems.map(item => item.orders.profile_id).filter(Boolean))];
      const profileEmailsMap = new Map();
      
      if (profileIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', profileIds);
        
        if (profilesData) {
          profilesData.forEach(profile => {
            profileEmailsMap.set(profile.id, profile.email);
          });
        }
      }

      // Fetch custom fields for all products in orders
      const productIds = [...new Set(orderItems.map(item => item.product_id).filter(Boolean))];
      const customFieldsMap = {};
      
      if (productIds.length > 0) {
        const { data: customFieldsData } = await supabase
          .from('product_custom_fields')
          .select('key_name, label, label_ar')
          .in('product_id', productIds);
        
        if (customFieldsData) {
          customFieldsData.forEach(field => {
            customFieldsMap[field.key_name] = {
              label: field.label,
              label_ar: field.label_ar
            };
          });
        }
      }
      
      setCustomFieldsMap(customFieldsMap);

      // Group order items by order ID
      const orderMap = new Map();
      
      orderItems.forEach(item => {
        const orderId = item.orders.id;
        const profileEmail = profileEmailsMap.get(item.orders.profile_id) || null;
        
        if (!orderMap.has(orderId)) {
          orderMap.set(orderId, {
            ...item.orders,
            // Add profile email
            profile: profileEmail ? { email: profileEmail } : null,
            order_items: []
          });
        }
        
        // Add this item to the order's items array
        // Ensure variant data is properly passed, including variant_options
        const variantData = item.variant ? {
          ...item.variant,
          variant_options: item.variant.variant_options || []
        } : null;
        
        orderMap.get(orderId).order_items.push({
          id: item.id,
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          line_total: item.line_total,
          unit_price: item.line_total / item.quantity,
          tracking_url: item.tracking_url,
          shipment_status_name: item.shipment_status_name,
          shipment_status_id: item.shipment_status_id,
          shipment_id: item.shipment_id,
          note: sanitizeOrderItemNote(item.note),
          attributes: item.attributes,
          product: item.products,
          product_name: item.products?.name || 'Unknown Product',
          sku: item.products?.sku || null,
          variant: variantData
        });
        
        // Set order-level shipment_status_name from the first item that has a status
        // This ensures the order status matches what's shown in the Status page
        const order = orderMap.get(orderId);
        if (item.shipment_status_name && !order.shipment_status_name) {
          order.shipment_status_name = item.shipment_status_name;
          order.tracking_url = item.tracking_url;
          order.shipment_id = item.shipment_id;
        }
      });

      // Convert map to array and attach seller-scoped totals (this admin only sees their brand's items)
      const transformedOrders = Array.from(orderMap.values()).map((order) => ({
        ...order,
        sellerSubtotal: sumLineTotals(order.order_items),
      }));

      // Sort by created_at descending (newest first)
      transformedOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      console.log('Transformed orders:', transformedOrders);
      console.log('First order sample:', transformedOrders[0]);
      if (transformedOrders[0]?.order_items?.[0]) {
        console.log('First order first item:', transformedOrders[0].order_items[0]);
        console.log('First order first item variant:', transformedOrders[0].order_items[0].variant);
        console.log('First order first item variant_options:', transformedOrders[0].order_items[0].variant?.variant_options);
      }
      setOrders(transformedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert(`Error fetching orders: ${error.message}`);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const exportToExcel = () => {
    if (filteredOrders.length === 0) {
      alert(isArabic ? 'لا توجد طلبات للتصدير' : 'No orders to export');
      return;
    }

    // Prepare data for Excel
    const excelData = filteredOrders.map(order => {
      const orderDate = new Date(order.created_at).toLocaleString(isArabic ? 'ar-OM' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      // Get order items summary
      const itemsSummary = order.order_items && order.order_items.length > 0
        ? order.order_items.map(item => `${item.product_name} (Qty: ${item.quantity})`).join('; ')
        : 'N/A';

      return {
        [isArabic ? 'رقم الطلب' : 'Order #']: formatOrderReference(order),
        [isArabic ? 'التاريخ' : 'Date']: orderDate,
        [isArabic ? 'اسم العميل' : 'Customer Name']: order.shipping_snapshot?.full_name || 'N/A',
        [isArabic ? 'هاتف العميل' : 'Customer Phone']: order.shipping_snapshot?.phone_number || 'N/A',
        [isArabic ? 'بريد العميل' : 'Customer Email']: (order.profile?.email || order.profiles?.email || order.shipping_snapshot?.email || 'N/A'),
        [isArabic ? 'عنوان الشحن' : 'Shipping Address']: order.shipping_snapshot 
          ? `${order.shipping_snapshot.area || ''}, ${order.shipping_snapshot.city || ''}, ${order.shipping_snapshot.governorate || ''}`
          : 'N/A',
        [isArabic ? 'عناصر الطلب' : 'Order Items']: itemsSummary,
        [isArabic ? 'إجمالي البائع' : 'Seller Total']: (order.sellerSubtotal ?? sumLineTotals(order.order_items)).toFixed(2),
        [isArabic ? 'العملة' : 'Currency']: order.currency || 'OMR',
        [isArabic ? 'حالة الطلب' : 'Order Status']: order.shipment_status_name || 'Pending',
        [isArabic ? 'حالة الدفع' : 'Payment Status']: isArabic 
          ? (order.payment_status === 'paid' ? 'مدفوع' : 'غير مدفوع')
          : order.payment_status,
        [isArabic ? 'طريقة الدفع' : 'Payment Method']: order.payment_method || 'N/A',
        [isArabic ? 'ملاحظات العميل' : 'Customer Notes']: order.customer_note || '',
        [isArabic ? 'ملاحظات الإدارة' : 'Admin Notes']: order.admin_note || ''
      };
    });

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, isArabic ? 'الطلبات' : 'Orders');

    // Set column widths
    const columnWidths = [
      { wch: 12 }, // Order #
      { wch: 20 }, // Date
      { wch: 20 }, // Customer Name
      { wch: 15 }, // Phone
      { wch: 25 }, // Email
      { wch: 30 }, // Address
      { wch: 40 }, // Items
      { wch: 14 }, // Seller Total
      { wch: 8 },  // Currency
      { wch: 15 }, // Order Status
      { wch: 15 }, // Payment Status
      { wch: 15 }, // Payment Method
      { wch: 30 }, // Customer Notes
      { wch: 30 }  // Admin Notes
    ];
    worksheet['!cols'] = columnWidths;

    // Generate Excel file and download
    const fileName = `orders_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const getWhatsAppUrl = (phoneNumber) => {
    if (!phoneNumber) return null;
    
    // Remove all non-digit characters except +
    let phone = phoneNumber.replace(/[^\d+]/g, '');
    
    // If phone doesn't start with +, assume it's a local number
    if (!phone.startsWith('+')) {
      // Remove leading zeros
      phone = phone.replace(/^0+/, '');
      // Add Oman country code if not present
      if (!phone.startsWith('968')) {
        phone = '968' + phone;
      }
      phone = '+' + phone;
    }
    
    // WhatsApp URL format: https://wa.me/PHONENUMBER (with country code, no +)
    const whatsappNumber = phone.replace(/^\+/, '');
    return `https://wa.me/${whatsappNumber}`;
  };

  const printReceipt = (order) => {
    const sellerSubtotal = order.sellerSubtotal ?? sumLineTotals(order.order_items);
    const printWindow = window.open('', '_blank');
    const orderDate = new Date(order.created_at).toLocaleString(isArabic ? 'ar-OM' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - Order ${formatOrderReference(order)}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
              color: #1f2937;
            }
            .header {
              margin-bottom: 30px;
              border-bottom: 2px solid #5efba9;
              padding-bottom: 20px;
            }
            .brand-row {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
            }
            .brand-logo {
              width: 88px;
              height: auto;
              object-fit: contain;
            }
            .header-text {
              text-align: right;
              flex: 1;
            }
            .header-text h1 {
              font-size: 24px;
              color: #1f2937;
              margin-bottom: 6px;
            }
            .header-text p {
              color: #6b7280;
              font-size: 14px;
              margin-bottom: 4px;
            }
            .order-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 30px;
            }
            .info-section h3 {
              font-size: 14px;
              color: #6b7280;
              margin-bottom: 8px;
              text-transform: uppercase;
            }
            .info-section p {
              font-size: 16px;
              color: #1f2937;
              font-weight: 500;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }
            .items-table th {
              background: #f3f4f6;
              padding: 12px;
              text-align: left;
              font-size: 12px;
              color: #6b7280;
              text-transform: uppercase;
              border-bottom: 2px solid #e5e7eb;
            }
            .items-table td {
              padding: 12px;
              border-bottom: 1px solid #e5e7eb;
            }
            .items-table tr:last-child td {
              border-bottom: none;
            }
            .total-section {
              margin-top: 20px;
              padding-top: 20px;
              border-top: 2px solid #e5e7eb;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              font-size: 14px;
            }
            .total-row.grand-total {
              font-size: 20px;
              font-weight: 700;
              color: #1f2937;
              margin-top: 12px;
              padding-top: 12px;
              border-top: 2px solid #5efba9;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              color: #6b7280;
              font-size: 12px;
              border-top: 1px solid #e5e7eb;
              padding-top: 20px;
            }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="brand-row">
              <img src="${window.location.origin}/logo-arabic.svg" alt="WASL Logo" class="brand-logo" />
              <div class="header-text">
                <h1>Order Receipt | إيصال الطلب</h1>
                <p>Easier Shopping, Faster Delivery | تسوق أسهل، توصيل أسرع</p>
              </div>
            </div>
          </div>
          
          <div class="order-info">
            <div class="info-section">
              <h3>${isArabic ? 'رقم الطلب' : 'Order Number'}</h3>
              <p>${formatOrderReference(order)}</p>
            </div>
            <div class="info-section">
              <h3>${isArabic ? 'التاريخ' : 'Date'}</h3>
              <p>${orderDate}</p>
            </div>
          </div>

          ${order.shipping_snapshot ? `
            <div class="info-section" style="margin-bottom: 30px;">
              <h3>${isArabic ? 'معلومات الشحن' : 'Shipping Information'}</h3>
              <p><strong>${isArabic ? 'الاسم' : 'Name'}:</strong> ${order.shipping_snapshot.full_name || 'N/A'}</p>
              <p><strong>${isArabic ? 'الهاتف' : 'Phone'}:</strong> ${order.shipping_snapshot.phone_number || 'N/A'}</p>
              <p><strong>${isArabic ? 'البريد الإلكتروني' : 'Email'}:</strong> ${(order.profile?.email || order.profiles?.email || order.shipping_snapshot?.email || 'N/A')}</p>
              <p><strong>${isArabic ? 'العنوان' : 'Address'}:</strong> ${order.shipping_snapshot.area || ''}, ${order.shipping_snapshot.city || ''}, ${order.shipping_snapshot.governorate || ''}</p>
            </div>
          ` : ''}

          <table class="items-table">
            <thead>
              <tr>
                <th>${isArabic ? 'المنتج' : 'Product'}</th>
                <th>${isArabic ? 'الكمية' : 'Quantity'}</th>
                <th>${isArabic ? 'السعر' : 'Price'}</th>
                <th>${isArabic ? 'المجموع' : 'Total'}</th>
              </tr>
            </thead>
            <tbody>
              ${order.order_items && order.order_items.map(item => `
                <tr>
                  <td><strong>${item.product_name}</strong></td>
                  <td>${item.quantity}</td>
                  <td>${item.unit_price.toFixed(2)} ${order.currency}</td>
                  <td><strong>${item.line_total.toFixed(2)} ${order.currency}</strong></td>
                </tr>
              `).join('') || ''}
            </tbody>
          </table>

          <div class="total-section">
            <div class="total-row grand-total">
              <span>${isArabic ? 'إجمالي البائع' : 'Seller Total'}:</span>
              <span>${sellerSubtotal.toFixed(2)} ${order.currency}</span>
            </div>
          </div>

          ${order.customer_note ? `
            <div style="margin-top: 30px; padding: 15px; background: #f9fafb; border-radius: 8px;">
              <strong>${isArabic ? 'ملاحظات العميل' : 'Customer Notes'}:</strong>
              <p style="margin-top: 8px;">${order.customer_note}</p>
            </div>
          ` : ''}

          <div class="footer">
            <p>Thank you for choosing WASL | شكراً لاختيارك وصل</p>
            <p style="margin-top: 8px;">©2025 WASL. All Rights Reserved | جميع الحقوق محفوظة</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const filteredOrders = orders.filter(order => {
    const orderDate = new Date(order.created_at);
    const startDateMatch = !startDate || orderDate >= new Date(`${startDate}T00:00:00`);
    const endDateMatch = !endDate || orderDate <= new Date(`${endDate}T23:59:59.999`);
    if (!startDateMatch || !endDateMatch) return false;

    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase().trim();
    const searchDigits = searchTerm.replace(/\D/g, ''); // Extract only digits from search term
    
    // If search term has digits, prioritize digit matching
    if (searchDigits.length > 0) {
      const orderIdStr = order.id.toString();
      const orderIdDigits = orderIdStr.replace(/\D/g, ''); // Extract only digits from order ID
      const orderNumStr = order.order_number != null ? String(order.order_number) : '';
      const phoneNumber = order.shipping_snapshot?.phone_number || '';
      const phoneDigits = phoneNumber.replace(/\D/g, ''); // Extract only digits from phone number
      const email = (order.profile?.email || order.profiles?.email) || '';
      const emailDigits = email.replace(/\D/g, ''); // Extract only digits from email
      
      return (
        orderNumStr.includes(searchDigits) ||
        orderIdDigits.includes(searchDigits) || // Match order ID by digits
        phoneDigits.includes(searchDigits) || // Match phone number by digits
        emailDigits.includes(searchDigits) || // Match email by digits
        order.id.toLowerCase().includes(searchLower) ||
        email.toLowerCase().includes(searchLower) ||
        order.status.toLowerCase().includes(searchLower)
      );
    } else {
      // Text-only search
      return (
        (order.order_number != null && String(order.order_number).toLowerCase().includes(searchLower)) ||
        order.id.toLowerCase().includes(searchLower) ||
        (order.profile?.email || order.profiles?.email)?.toLowerCase().includes(searchLower) ||
        order.status.toLowerCase().includes(searchLower)
      );
    }
  });

  // Reset displayed count when search changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [searchTerm, startDate, endDate]);

  // Slice to show only displayedCount items
  const displayedOrders = filteredOrders.slice(0, displayedCount);
  const hasMoreOrders = filteredOrders.length > displayedCount;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Get old status before updating
      const { data: oldOrder } = await supabase
        .from('orders')
        .select('status')
        .eq('id', orderId)
        .single();

      const oldStatus = oldOrder?.status || null;

      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) {
        console.error('Error updating order status:', error);
        alert(isArabic ? 'فشل تحديث حالة الطلب. يرجى المحاولة مرة أخرى.' : 'Failed to update order status. Please try again.');
        return;
      }

      // Trigger order status email if status changed
      if (oldStatus !== newStatus) {
        try {
          const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://lxprwdplyftigaemzaua.supabase.co';
          const functionUrl = `${supabaseUrl}/functions/v1/send-order-status-email`;
          
          const { data: { session } } = await supabase.auth.getSession();
          
          fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imx4cHJ3ZHBseWZ0aWdhZW16YXVhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjE1NDEwMjUsImV4cCI6MjA3NzExNzAyNX0.-n4yL0Nsm5sgNiu8SqwyAVZQ3x2CyT_tEiYxeRJ_c8I',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderId: orderId,
              newStatus: newStatus,
              oldStatus: oldStatus,
            }),
          }).catch(err => {
            console.error('Failed to send order status email (non-blocking):', err);
          });
        } catch (emailError) {
          console.error('Error triggering order status email:', emailError);
        }
      }

      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
    } catch (error) {
      console.error('Error updating order status:', error);
      alert(isArabic ? 'فشل تحديث حالة الطلب. يرجى المحاولة مرة أخرى.' : 'Failed to update order status. Please try again.');
    }
  };

  const renderOrderExpandedContent = (order) => (
    <>
      <ActionButtonsContainer>
        <PrintButton onClick={() => printReceipt(order)}>
          <FiPrinter /> {isArabic ? 'طباعة الإيصال' : 'Print Receipt'}
        </PrintButton>
        {order.shipping_snapshot?.phone_number && getWhatsAppUrl(order.shipping_snapshot.phone_number) && (
          <WhatsAppButton
            href={getWhatsAppUrl(order.shipping_snapshot.phone_number)}
            target="_blank"
            rel="noopener noreferrer"
            title={isArabic ? 'تواصل عبر واتساب' : 'Contact via WhatsApp'}
          >
            <FaWhatsapp /> {isArabic ? 'واتساب' : 'WhatsApp'}
          </WhatsAppButton>
        )}
      </ActionButtonsContainer>
      <ExpandedSection>
        <SectionTitle>{isArabic ? 'عناصر الطلب' : 'Order Items'} ({order.order_items?.length || 0})</SectionTitle>
        {order.order_items && order.order_items.length > 0 ? (
          order.order_items.map(item => (
            <ItemRow key={item.id}>
              <ItemDetails>
                <ItemImage
                  src={item.product?.images?.[0] || 'https://via.placeholder.com/48'}
                  alt={item.product_name}
                />
                <ItemInfo>
                  <ItemName>{item.product_name}</ItemName>
                  <ItemMeta>
                    {isArabic ? 'رمز المنتج' : 'SKU'}: {item.variant?.sku || item.sku || (isArabic ? 'غير متوفر' : 'N/A')} • {isArabic ? 'الكمية' : 'Qty'}: {item.quantity} • {item.unit_price.toFixed(2)} {isArabic ? 'ريال عماني لكل قطعة' : 'OMR each'}
                  </ItemMeta>
                  {item.variant && (
                    <div style={{ marginTop: '4px' }}>
                      {item.variant.variant_options && item.variant.variant_options.length > 0 ? (
                        <>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                            {isArabic ? 'المتغير المحدد' : 'Selected Variant'}:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {item.variant.variant_options.map((vo, idx) => (
                              <span key={idx} style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                background: '#dcfce7',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#059669',
                                fontWeight: 500
                              }}>
                                {vo.option_group?.label || vo.option_group?.name || 'Option'}: {vo.option_value?.value || 'N/A'}
                              </span>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {isArabic ? 'المتغير' : 'Variant'}: {item.variant.sku || item.variant.id || (isArabic ? 'غير متوفر' : 'N/A')}
                        </div>
                      )}
                    </div>
                  )}
                  {item.note && (
                    <ItemMeta style={{ marginTop: '4px', fontStyle: 'italic', color: '#4b5563' }}>
                      {isArabic ? 'ملاحظة' : 'Note'}: {item.note}
                    </ItemMeta>
                  )}
                  {item.attributes && typeof item.attributes === 'object' && Object.keys(item.attributes).length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#374151' }}>
                      <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1f2937' }}>
                        {isArabic ? 'معلومات إضافية' : 'Additional Information'}:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        {Object.entries(item.attributes).map(([key, value]) => {
                          if (value === null || value === undefined || value === '') return null;

                          const fieldInfo = customFieldsMap[key];
                          const displayLabel = fieldInfo
                            ? (isArabic && fieldInfo.label_ar ? fieldInfo.label_ar : fieldInfo.label)
                            : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                          let displayValue = value;
                          if (Array.isArray(value)) {
                            displayValue = value.join(', ');
                          } else if (typeof value === 'object') {
                            displayValue = JSON.stringify(value, null, 2);
                          } else {
                            displayValue = String(value);
                          }

                          return (
                            <div key={key} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 500, minWidth: '100px' }}>{displayLabel}:</span>
                              <span style={{ color: '#6b7280' }}>{displayValue}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </ItemInfo>
              </ItemDetails>
              <div style={{ fontWeight: 600 }}>
                {item.line_total.toFixed(2)} {isArabic ? 'ريال عماني' : 'OMR'}
              </div>
            </ItemRow>
          ))
        ) : (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            {isArabic ? 'لا توجد عناصر في هذا الطلب' : 'No items in this order'}
          </p>
        )}
      </ExpandedSection>

      <ExpandedSection>
        <SectionTitle>{isArabic ? 'معلومات الشحن' : 'Shipping Information'}</SectionTitle>
        {order.shipping_snapshot && Object.keys(order.shipping_snapshot).length > 0 ? (
          <div style={{ fontSize: '14px', color: '#374151' }}>
            <div><strong>{isArabic ? 'الاسم' : 'Name'}:</strong> {order.shipping_snapshot.full_name || (isArabic ? 'غير متوفر' : 'N/A')}</div>
            <div><strong>{isArabic ? 'الهاتف' : 'Phone'}:</strong> {order.shipping_snapshot.phone_number || (isArabic ? 'غير متوفر' : 'N/A')}</div>
            <div><strong>{isArabic ? 'البريد الإلكتروني' : 'Email'}:</strong> {(order.profile?.email || order.profiles?.email || order.shipping_snapshot?.email || (isArabic ? 'غير متوفر' : 'N/A'))}</div>
            <div><strong>{isArabic ? 'العنوان' : 'Address'}:</strong> {order.shipping_snapshot.area}, {order.shipping_snapshot.city}, {order.shipping_snapshot.governorate}</div>
            {order.shipping_snapshot.delivery_notes && (
              <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
                {isArabic ? 'ملاحظات' : 'Notes'}: {order.shipping_snapshot.delivery_notes}
              </div>
            )}
          </div>
        ) : (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            {isArabic ? 'لا توجد معلومات شحن متاحة' : 'No shipping information available'}
          </p>
        )}
      </ExpandedSection>

      {order.customer_note && (
        <ExpandedSection>
          <SectionTitle>{isArabic ? 'ملاحظات العميل' : 'Customer Notes'}</SectionTitle>
          <p style={{ color: '#374151', fontSize: '14px' }}>{order.customer_note}</p>
        </ExpandedSection>
      )}

      {order.admin_note && (
        <ExpandedSection>
          <SectionTitle>{isArabic ? 'ملاحظات الإدارة' : 'Admin Notes'}</SectionTitle>
          <p style={{ color: '#374151', fontSize: '14px' }}>{order.admin_note}</p>
        </ExpandedSection>
      )}
    </>
  );

  if (loading) {
    return (
      <Container>
        <EmptyState>{isArabic ? 'جاري تحميل الطلبات...' : 'Loading orders...'}</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FiPackage /> {isArabic ? 'الطلبات' : 'Orders'}
        </Title>
        <HeaderActions>
          <SearchBar>
            <FiSearch />
            <SearchInput
              type="text"
              placeholder={isArabic ? 'ابحث عن الطلبات...' : 'Search orders...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <DateInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label={isArabic ? 'من تاريخ' : 'From date'}
            title={isArabic ? 'من تاريخ' : 'From date'}
          />
          <DateInput
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label={isArabic ? 'إلى تاريخ' : 'To date'}
            title={isArabic ? 'إلى تاريخ' : 'To date'}
          />
          <IconButton onClick={() => { setStartDate(''); setEndDate(''); }}>
            {isArabic ? 'مسح التاريخ' : 'Clear Dates'}
          </IconButton>
          <IconButton onClick={exportToExcel}>
            <FiDownload /> {isArabic ? 'تحميل Excel' : 'Download Excel'}
          </IconButton>
          <IconButton onClick={fetchOrders}>
            <FiRefreshCw /> {isArabic ? 'تحديث' : 'Refresh'}
          </IconButton>
        </HeaderActions>
      </Header>

      <Card>
        <DesktopOnly>
        <TableContainer>
          <Table $isArabic={isArabic}>
            <thead>
              <TableRow>
                <TableHeaderCell $isArabic={isArabic}></TableHeaderCell>
                <TableHeaderCell $isArabic={isArabic}>{isArabic ? 'رقم الطلب' : 'Order #'}</TableHeaderCell>
                <TableHeaderCell $isArabic={isArabic}>{isArabic ? 'العميل' : 'Customer'}</TableHeaderCell>
                <TableHeaderCell $isArabic={isArabic}>{isArabic ? 'رقم الهاتف' : 'Phone'}</TableHeaderCell>
                <TableHeaderCell $isArabic={isArabic}>{isArabic ? 'التاريخ' : 'Date'}</TableHeaderCell>
                <TableHeaderCell $isArabic={isArabic}>{isArabic ? 'إجمالي البائع' : 'Seller Total'}</TableHeaderCell>
                <TableHeaderCell $isArabic={isArabic}>{isArabic ? 'الحالة' : 'Status'}</TableHeaderCell>
                <TableHeaderCell $isArabic={isArabic}>{isArabic ? 'حالة الدفع' : 'Payment Status'}</TableHeaderCell>
              </TableRow>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} style={{ textAlign: 'center', padding: '64px' }}>
                    <EmptyIcon>📦</EmptyIcon>
                    <p>{isArabic ? 'لم يتم العثور على طلبات' : 'No orders found'}</p>
                  </TableCell>
                </TableRow>
              ) : (
                displayedOrders.map(order => {
                  const isExpanded = expandedOrders.has(order.id);

                  return (
                    <React.Fragment key={order.id}>
                      <TableRow>
                        <TableCell $isArabic={isArabic}>
                          <ExpandButton onClick={() => toggleExpanded(order.id)}>
                            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                          </ExpandButton>
                        </TableCell>
                        <TableCell $isArabic={isArabic}>
                          <strong>{formatOrderReference(order)}</strong>
                        </TableCell>
                        <TableCell $isArabic={isArabic}>{order.shipping_snapshot?.full_name || (isArabic ? 'غير متوفر' : 'N/A')}</TableCell>
                        <TableCell $isArabic={isArabic}>
                          {order.shipping_snapshot?.phone_number || (isArabic ? 'غير متوفر' : 'N/A')}
                        </TableCell>
                        <TableCell $isArabic={isArabic}>{formatDate(order.created_at)}</TableCell>
                        <TableCell $isArabic={isArabic}>{(order.sellerSubtotal ?? sumLineTotals(order.order_items)).toFixed(2)} {order.currency}</TableCell>
                        <TableCell $isArabic={isArabic}>
                          <StatusBadge status={getStatusTone(order.shipment_status_name)}>
                            {(order.shipment_status_name == "" || !order.shipment_status_name)
                              ? (isArabic ? "قيد الانتظار" : "Pending")
                              : normalizeStatusName(order.shipment_status_name, isArabic)}
                          </StatusBadge>
                        </TableCell>
                        <TableCell $isArabic={isArabic}>
                          <StatusBadge status={order.payment_status}>
                            {isArabic ? (order.payment_status === 'paid' ? 'مدفوع' : 'غير مدفوع') : order.payment_status}
                          </StatusBadge>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell $isArabic={isArabic} colSpan={8}>
                            <ExpandedContent>
                              {renderOrderExpandedContent(order)}
                            </ExpandedContent>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </TableContainer>
        </DesktopOnly>

        <MobileOnly>
          {filteredOrders.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📦</EmptyIcon>
              <p>{isArabic ? 'لم يتم العثور على طلبات' : 'No orders found'}</p>
            </EmptyState>
          ) : (
            <MobileOrderList>
              {displayedOrders.map((order) => {
                const isExpanded = expandedOrders.has(order.id);
                const orderStatusLabel = (order.shipment_status_name == "" || !order.shipment_status_name)
                  ? (isArabic ? 'قيد الانتظار' : 'Pending')
                  : normalizeStatusName(order.shipment_status_name, isArabic);

                return (
                  <MobileOrderCard key={order.id}>
                    <MobileOrderHeader type="button" onClick={() => toggleExpanded(order.id)}>
                      <MobileOrderHeaderMain>
                        <MobileOrderId>{formatOrderReference(order)}</MobileOrderId>
                        <MobileOrderMeta>{order.shipping_snapshot?.full_name || (isArabic ? 'غير متوفر' : 'N/A')}</MobileOrderMeta>
                        <MobileOrderMeta>{order.shipping_snapshot?.phone_number || (isArabic ? 'غير متوفر' : 'N/A')}</MobileOrderMeta>
                        <MobileOrderMeta>{formatDate(order.created_at)}</MobileOrderMeta>
                      </MobileOrderHeaderMain>
                      <MobileOrderHeaderAside>
                        <MobileOrderTotal>{(order.sellerSubtotal ?? sumLineTotals(order.order_items)).toFixed(2)} {order.currency}</MobileOrderTotal>
                        <MobileBadgeRow>
                          <StatusBadge status={getStatusTone(order.shipment_status_name)}>
                            {orderStatusLabel}
                          </StatusBadge>
                          <StatusBadge status={order.payment_status}>
                            {isArabic ? (order.payment_status === 'paid' ? 'مدفوع' : 'غير مدفوع') : order.payment_status}
                          </StatusBadge>
                        </MobileBadgeRow>
                        {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                      </MobileOrderHeaderAside>
                    </MobileOrderHeader>
                    {isExpanded && (
                      <MobileOrderBody>
                        <ExpandedContent>
                          {renderOrderExpandedContent(order)}
                        </ExpandedContent>
                      </MobileOrderBody>
                    )}
                  </MobileOrderCard>
                );
              })}
            </MobileOrderList>
          )}
        </MobileOnly>
      </Card>

      {!loading && hasMoreOrders && (
        <LoadMoreButton onClick={() => setDisplayedCount(prev => prev + 20)}>
          {isArabic ? 'تحميل المزيد' : 'Load More'} ({filteredOrders.length - displayedCount} {isArabic ? 'متبقي' : 'remaining'})
        </LoadMoreButton>
      )}
    </Container>
  );
}

export default AdminOrders;
